/**
 * The marketing page.
 *
 * The thesis: a customer types what their business does, and the address their website will live at
 * assembles itself underneath as they type. That is the product's own first step used as the page's
 * argument — the thing you would do next is already on the screen, so there is nothing to explain
 * before it.
 *
 * ⚠️ THE PRICE IS THE OTHER HALF OF THE ARGUMENT, and it is stated in full rather than teased. This
 * customer has been quoted three thousand riyals by an agency; the number is the offer, so hiding it
 * behind a "see plans" link would be hiding the only thing that matters.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Button, Notice, Price, Spinner, useLang } from '@ksa/ui'
import { SAMPLES, matchSample } from '@ksa/model'
import { ApiFailure, api } from '../api.ts'
import { destinationFor } from '../generated.ts'
import { useLoad } from '../useLoad.ts'
import { useTitle } from '../useTitle.ts'
import './landing.css'

/*
 * ⚠️ `guessHandle()` LIVED HERE AND IS DELETED. It took the first few words — "We roast coffee in Al
 * Olaya…" became `we-roast-coffee` — and showed it as the address the customer was about to get.
 * It was wrong twice: a slug is TRANSLATED by the model, not chopped out of the input (§2.5), and
 * the address a generated site actually receives comes from the model's plan, so this preview was
 * announcing a name that would never exist. A convincing lie about the thing being sold.
 *
 * The sample's own model-generated handle is shown while a sample is showing; the moment somebody
 * types their own words the field says `your-name`, because at that point the honest answer is that
 * we do not know yet.
 */


/**
 * ⚠️ THE EXAMPLES LIVE IN `@ksa/model` NOW, WITH THE SITE EACH ONE PRODUCES BESIDE IT. They were five
 * strings and five addresses here, and pressing the button asked the model to write the site from
 * them — about half a minute and a bill, every time, for five sentences that never change. The
 * description, the address and the document are one declaration in `seed/samples.ts`; this page shows
 * the first two and hands the third to the editor.
 *
 * ⚠️ THEY ARE STILL THE TEXTAREA'S VALUE, NOT ITS PLACEHOLDER, AND THAT IS STILL THE ENTIRE POINT. A
 * placeholder is decoration: the Build button stays disabled and a visitor who likes what they see
 * has to retype it. As a value, the very first thing on the screen is a complete, buildable idea and
 * the button is live.
 */

/**
 * Where a half-written description waits out a reload.
 *
 * ⚠️ A DIFFERENT KEY FROM `ksa:pending-description`, WHICH IS THE SIGN-IN HANDOVER. That one is
 * written once, consumed by `/sites?build=1` and deleted; this one is rewritten on every keystroke
 * and outlives nothing but a refresh. One key doing both jobs would mean a reload resurrecting a
 * description that had already been built into a site.
 */
const DRAFT_KEY = 'ksa:landing-draft'

function readDraft(): string {
  try {
    return sessionStorage.getItem(DRAFT_KEY) ?? ''
  } catch {
    return ''
  }
}

function clearDraft(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY)
  } catch {
    /* nothing to clear */
  }
}

/**
 * ⚠️ ASKED EVERY TIME RATHER THAN CACHED. A visitor can turn this on mid-visit — an operating system
 * setting, not a page load — and a value read once at mount keeps animating at somebody who has just
 * asked it to stop.
 */
function reducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

/** How long each example sits on screen once it has finished typing itself out. */
const ROTATE_MS = 5000

/**
 * The example types itself out when it changes.
 *
 * ⚠️ IT ERASES AND RETYPES RATHER THAN SWAPPING, AND THE REASON IS THAT THE SWAP WAS INVISIBLE. One
 * paragraph of Arabic replaced by another paragraph of Arabic, instantly, five seconds apart, reads
 * as a rendering glitch to anybody not staring at it — and what it is actually saying is "there is
 * more than one thing you could put in this box". Typing says that, because a visitor watches the
 * sentence being written and understands that they are looking at an EXAMPLE of an input rather than
 * at fixed copy.
 *
 * ⚠️ THE FIRST ONE IS NOT ANIMATED. The box has to be full and the button live in the first frame —
 * that is what makes an arriving visitor one click from a website (see the note on SAMPLES above), and
 * a box that types itself out on load is a box that is empty for two seconds and a button that is
 * disabled for two seconds.
 *
 * ⚠️ AND IT REPORTS WHETHER IT IS STILL TYPING, because the address underneath types with it and
 * wants the caret while it does.
 */
function useTypewriter(target: string, animate: boolean): { shown: string; typing: boolean } {
  const [shown, setShown] = useState(target)
  const [typing, setTyping] = useState(false)
  // What is on screen right now, readable from inside a timer without re-arming it every character.
  const onScreen = useRef(target)
  const first = useRef(true)

  useEffect(() => {
    onScreen.current = shown
  }, [shown])

  useEffect(() => {
    if (!animate || first.current) {
      first.current = false
      setTyping(false)
      setShown(target)
      onScreen.current = target
      return
    }

    let cancelled = false
    setTyping(true)

    /*
     * Erase in chunks and type one character at a time. Deleting is something a person does quickly
     * and without looking; writing is the part worth watching, and it is the part that says what
     * this box is for.
     */
    const TICK_MS = 14
    const ERASE_CHUNK = 4

    const timer = setInterval(() => {
      if (cancelled) return
      const current = onScreen.current
      if (current.length > 0 && !target.startsWith(current)) {
        const shorter = current.slice(0, Math.max(0, current.length - ERASE_CHUNK))
        onScreen.current = shorter
        setShown(shorter)
        return
      }
      if (current.length >= target.length) {
        setTyping(false)
        clearInterval(timer)
        return
      }
      const next = target.slice(0, current.length + 1)
      onScreen.current = next
      setShown(next)
      // ⚠️ ONE TICK FOR BOTH PHASES, WITH ERASING TAKING BIGGER BITES. Two intervals at two speeds
      // is two timers to cancel and a seam between them; four characters a tick is what makes
      // erasing read as quick and typing read as written.
    }, TICK_MS)

    return () => {
      cancelled = true
      clearInterval(timer)
      // ⚠️ THE TARGET IS PUT UP WHOLE ON THE WAY OUT. This effect is torn down when the visitor takes
      // the box (they focused it, or typed) — and handing them a half-typed sentence to edit, or
      // submitting one, is the one outcome the animation must not produce.
      setTyping(false)
      setShown(target)
      onScreen.current = target
    }
  }, [target, animate])

  return { shown, typing }
}

export function Landing({ signedIn }: { signedIn: boolean }) {
  const { t, lang } = useLang()
  const navigate = useNavigate()
  useTitle('A website for your business, in minutes', 'موقع لنشاطك التجاري في دقائق')
  /**
   * ⚠️ WHAT SOMEBODY TYPED SURVIVES A RELOAD, BECAUSE THE PAGE CAN NOW RELOAD ITSELF. The deploy
   * watcher reloads a tab when a new build ships (see `useDeployWatch`), and doing that to somebody
   * halfway through describing their business would be a worse bug than the stale build it fixes.
   * The watcher already refuses to reload over unsaved EDITOR work; this page has no save, so it
   * keeps the text instead.
   *
   * ⚠️ ONLY WHAT THEY TYPED, NEVER THE ROTATING EXAMPLE. Restoring a sample would freeze the
   * rotation on whichever one happened to be showing when the tab reloaded — turning a feature into
   * an apparently broken one, for somebody who never touched the box.
   */
  const [sample, setSample] = useState(0)
  /** One-way: the moment somebody touches the box it is theirs, and nothing rotates under them. */
  const [taken, setTaken] = useState(() => Boolean(readDraft()))
  const [typed, setTyped] = useState(() => readDraft())
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [ai] = useLoad((signal) => api.aiAvailable(signal), [])

  /**
   * ⚠️ THE ROTATION STOPS ON FOCUS, ON TYPING, AND FOR ANYBODY WHO ASKED FOR LESS MOTION. The first
   * two are obvious — text changing under a cursor is hostile. The third is `prefers-reduced-motion`:
   * a box of text that rewrites itself every five seconds is exactly the kind of unrequested movement
   * that setting exists to stop, and honouring it costs one line.
   */
  useEffect(() => {
    if (taken) return
    if (reducedMotion()) return
    const timer = setInterval(() => setSample((i) => (i + 1) % SAMPLES.length), ROTATE_MS)
    return () => clearInterval(timer)
  }, [taken])

  // Persist as they type. `sessionStorage`, not `localStorage`: this is for surviving a reload of
  // THIS tab, not for greeting somebody a week later with a half-finished sentence they have
  // forgotten writing.
  useEffect(() => {
    if (!taken) return
    try {
      sessionStorage.setItem(DRAFT_KEY, typed)
    } catch {
      /* storage disabled — the reload simply loses it, which is the old behaviour */
    }
  }, [taken, typed])

  // What is actually in the box: their words once they have any, otherwise the current example.
  const current = SAMPLES[sample] ?? SAMPLES[0]!
  const description = taken ? typed : lang === 'ar' ? current.description.ar : current.description.en

  /*
   * ⚠️ THE ANIMATION IS A VIEW OF `description`, NOT A REPLACEMENT FOR IT. What gets submitted, what
   * the button's length check reads and what `matchSample` is asked about is always the WHOLE
   * sentence; only the pixels lag. A half-typed example reaching the model — or, worse, failing to
   * match an example and being sent for generation — is the bug this separation exists to make
   * impossible.
   */
  const animate = !taken && !reducedMotion()
  const { shown, typing } = useTypewriter(description, animate)
  const { shown: shownHandle } = useTypewriter(current.handle, animate)

  /**
   * ⚠️ THE SAMPLE'S ADDRESS IS SHOWN WHILE THE SAMPLE IS SHOWING, AND HANDED OVER WHOLE WHEN THEY
   * TAKE THE BOX. It is part of the example — a complete idea including what it would be called —
   * and it gives somebody a shape to edit rather than an empty field to invent one in.
   */
  // The example's own address, shown while the example is shown. Once somebody writes their own
  // description there is nothing honest to display, so the last example's address stays as the
  // illustration rather than being replaced by a guess about their words.
  const aiOn = ai.state === 'ready' && ai.data.available
  /**
   * ⚠️ WHETHER *THIS* VISITOR MAY BUILD, WHICH IS NOT THE SAME QUESTION AS WHETHER THE BUILDER WORKS.
   * Free building has a day's budget and a concurrency cap (`ai/limits.ts`), and a signed-in customer
   * passes through both untouched. Asking before the button is pressed is the difference between a
   * refusal and half a minute of waiting followed by a refusal.
   */
  const canBuild = aiOn && (signedIn || ai.data.anonymous)
  // Whether the words in the box are still one of ours. This is the entire gate between showing a
  // demonstration and spending a model call — see `build`.
  const asSample = matchSample(description)

  const build = async () => {
    /**
     * ⚠️ PRESSING THE BUTTON FREEZES THE BOX. Rotation stopped on focus and on typing but not on
     * SUBMIT — so somebody who read an example, liked it and pressed the button watched the text
     * change underneath them while the request was in flight, and on the sign-in path came back to a
     * different example than the one they chose. What was submitted and what is on screen must be
     * the same sentence.
     */
    if (!taken) {
      setTyped(description)
      setTaken(true)
    }

    /**
     * ⚠️ AN EXAMPLE IS SHOWN, NOT BUILT — AND NOBODY IS ASKED TO SIGN IN FOR IT. These five sites are
     * already written (`@ksa/model`'s `SAMPLES`), so there is nothing to generate, nothing to wait
     * for and nothing to charge for. Sending a visitor to an identity provider before they have seen
     * a single page of what this product makes is asking for a commitment in exchange for a promise.
     *
     * ⚠️ AND IT IS NOT SAVED EITHER. The demonstration lives in this tab until the visitor changes
     * something and says to keep it — see `DemoEditor`. A site created for everybody who pressed the
     * button on an example is a database full of five identical flower shops.
     */
    if (asSample) {
      clearDraft()
      navigate(`/demo/${asSample.id}`)
      return
    }

    /**
     * > ⚠️ **THIS SENT A SIGNED-OUT VISITOR TO THE IDENTITY PROVIDER, AND IT NO LONGER DOES.** The
     * > reasoning was sound and is kept: words that are not one of ours cost a model call, and a
     * > sign-in was the only thing standing between an endpoint that spends tokens and anybody who
     * > can hold down a button.
     * >
     * > What it did not account for is that a sign-in was doing FOUR jobs at once — identify the
     * > caller, bound the bill, bound the load, and give abuse somewhere to land — and paying for
     * > all four with the product's only demonstrable feature. A stranger's first experience of a
     * > website builder was a passkey ceremony, and for anybody without a passkey it was a dead end
     * > (§4.2). Those four jobs are now four limits in `ai/limits.ts`, and building is free.
     *
     * ⚠️ WHAT IS STILL GATED IS KEEPING IT. Nothing is stored for a stranger: the document comes back
     * in the reply, lives in this tab, and becomes a site the moment they say so — which is the same
     * arrangement the examples above already use, and the reason there was almost nothing to build
     * here (see `destinationFor` and `useKeepDemo`).
     */
    setBusy(true)
    setFailure(null)
    try {
      const created = await api.generate(description, lang)
      clearDraft()
      navigate(destinationFor(created))
    } catch (error) {
      setFailure(error instanceof ApiFailure ? error : null)
      setBusy(false)
    }
  }

  /**
   * ⚠️ HALF A MINUTE IS TOO LONG FOR A SPINNER INSIDE A BUTTON. Generation takes about thirty seconds
   * and this page is now where it happens for most people — a disabled button with a dot in it, on a
   * marketing page, reads as a page that has stopped working. The sites list already had this screen
   * for the same wait; it says how long, because a wait with a stated length is a wait people sit
   * through and an unexplained one is a tab they close.
   */
  if (busy) {
    return (
      <div className="u-page u-section u-stack" style={{ textAlign: 'center', alignItems: 'center' }}>
        <Spinner label={t('Building your website', 'جارٍ بناء موقعك')} />
        <h1 className="u-title">{t('Building your website', 'جارٍ بناء موقعك')}</h1>
        <p className="u-lead">
          {/*
            ⚠️ IT NO LONGER SAYS "BOTH LANGUAGES" TO SOMEBODY WHO IS NOT GOING TO GET BOTH. A site
            built without an account is written in one language and the other is filled in when it is
            wanted (§5.15) — so the sentence that was true for everybody is now true for customers,
            and the screen that makes the claim is the one that has to stop making it. It is also the
            honest place to set the expectation, because the second language arriving later is a
            thing that happens on screen a minute from now.
          */}
          {signedIn
            ? t(
                'Writing the pages in both languages. This takes about half a minute.',
                'نكتب الصفحات باللغتين. يستغرق هذا نصف دقيقة تقريبًا.',
              )
            : t(
                'Writing the pages in English. The Arabic is written when you switch to it.',
                'نكتب الصفحات بالعربية. تُكتب الإنجليزية عند التبديل إليها.',
              )}
        </p>
      </div>
    )
  }

  return (
    <>
      <section className="lp-hero">
        <div className="u-page lp-hero__inner">
          {/*
            > ⚠️ AN EYEBROW ("For Saudi small businesses") AND A LEAD PARAGRAPH ("Describe what you do.
            > You get a real website…") STOOD HERE AND ARE DELETED. Both said what the box below them
            > already demonstrates: the box is full of an example describing a Saudi small business,
            > so a sentence explaining that the box is for describing a Saudi small business is the
            > page talking about itself. A headline, the thing itself, and the price.
          */}
          <h1 className="u-display" style={{ marginBlockStart: 'var(--u-s3)' }}>
            {t('Your business deserves a website.', 'نشاطك يستحق موقعًا إلكترونيًا.')}
          </h1>

          <div className="lp-ask">
            <label className="u-sr" htmlFor="describe">
              {t('What does your business do?', 'ماذا يعمل نشاطك التجاري؟')}
            </label>
            <textarea
              id="describe"
              className="lp-ask__input"
              value={taken ? typed : shown}
              onChange={(e) => {
                setTaken(true)
                setTyped(e.target.value)
              }}
              onFocus={() => {
                // Stop rotating, and keep whatever was showing — so a visitor who clicked in because
                // they liked this one can edit it rather than watch it be replaced. The address comes
                // across with it, for the same reason.
                if (!taken) {
                  setTyped(description)
                  setTaken(true)
                }
              }}
              rows={3}
            />

            <div className="lp-ask__foot">
              {/*
                ⚠️ ILLUSTRATIVE, AND IT BELONGS TO THE SAMPLE RATHER THAN TO THE VISITOR. It has been
                three things in one day: the first few words of the description, then a model-written
                slug, then a box for the customer to fill in. All three were solving a problem that
                does not exist at this point in the funnel — nobody needs an address to look at what
                they are about to get. The preview needs no domain and the domain is not settled
                until payment, so here it is part of the example: this is what one of these looks
                like.

                ⚠️ ONE IDENTIFIER, ISOLATED AS A WHOLE. Marking only the suffix leaves the row in the
                RTL flow and the domain renders backwards — see `.u-idgroup`.
              */}
              <span className="lp-url u-idgroup" aria-hidden="true">
                <span className="lp-url__handle">{taken ? current.handle : shownHandle}</span>
                {/* ⚠️ THE CARET BELONGS TO THE ADDRESS, NOT TO THE TEXTAREA. A textarea cannot carry
                    a decorative caret without a second copy of its text laid over it, and the box
                    already has a real one the moment anybody clicks in. Here it is the thing that
                    says the address is being written for you. */}
                {typing && !taken && <span className="lp-url__caret" />}
                <span className="lp-url__rest">.ksa-web.app</span>
              </span>

              <div className="u-spacer" />

              <Button
                size="lg"
                busy={busy}
                /* ⚠️ AN EXAMPLE IS NOT SUBJECT TO THE MODEL BEING CONFIGURED. Its site is already
                   written, so a deployment with no key can still show one — which is the whole
                   point of having them. */
                disabled={description.trim().length < 10 || (!asSample && !canBuild)}
                onClick={build}
              >
                {t('Build my website', 'ابنِ موقعي')}
              </Button>
            </div>

            {failure && (
              <div style={{ marginBlockStart: 'var(--u-s4)' }}>
                <Notice tone="error">{lang === 'ar' ? failure.text.ar : failure.text.en}</Notice>
              </div>
            )}

            {/*
              ⚠️ THE DOOR IS SHUT RATHER THAN THE BUILDER BROKEN, AND THOSE ARE DIFFERENT SENTENCES
              WITH DIFFERENT WAYS OUT. This one has one: a signed-in customer has their own allowance,
              so it is worth saying so here rather than leaving a disabled button with no explanation.
            */}
            {ai.state === 'ready' && aiOn && !canBuild && !asSample && (
              <div style={{ marginBlockStart: 'var(--u-s4)' }}>
                <Notice tone="warn">
                  {t(
                    'A lot of people are building right now. Sign in to build straight away — it is free — or try one of the examples.',
                    'عدد كبير من الناس ينشئون مواقع الآن. سجّل الدخول للبناء فورًا — الأمر مجاني — أو جرّب أحد الأمثلة.',
                  )}{' '}
                  <a className="u-link" href={`/auth/start?intent=register&returnTo=${encodeURIComponent('/sites')}`}>
                    {t('Sign in', 'تسجيل الدخول')}
                  </a>
                </Notice>
              </div>
            )}

            {ai.state === 'ready' && !aiOn && !asSample && (
              <div style={{ marginBlockStart: 'var(--u-s4)' }}>
                {/*
                  ⚠️ THE COMPONENT THAT KNOWS SAYS SO. This deployment has no model key, and the
                  honest thing is to say it here — beside the button that does not work — rather
                  than to disable the button silently or to put a banner in the shell that somebody
                  forgets to remove the day a key is configured.
                */}
                <Notice tone="warn">
                  {t(
                    'Automatic building is switched off on this deployment. You can still open one of the examples, or start from a template.',
                    'البناء التلقائي معطّل في هذه النسخة. لا يزال بإمكانك فتح أحد الأمثلة أو البدء من قالب.',
                  )}
                </Notice>
              </div>
            )}
          </div>

          {/*
            ⚠️ THE OFFER MOVED AND THE SENTENCE HAD TO MOVE WITH IT. It said "you pay when you
            publish", which stopped being true: publishing is free for a week and what is bought is
            the site STAYING up (`TRIAL`). A marketing line quoting the old funnel is worse than a
            vague one — the customer builds a site expecting a wall that is not there and then meets
            one they were not told about.

            ⚠️ AND THE PRICE IS STILL HERE, which is not a contradiction of "no price until the
            checkout". That instruction is about the PUBLISH FLOW, where a number would be a second
            decision in the middle of the first. Here it is the offer itself: this customer has been
            quoted three thousand riyals by an agency, and hiding the number behind "see plans" would
            be hiding the only thing that matters (§5.7).
          */}
          <p className="u-small u-faint" style={{ marginBlockStart: 'var(--u-s4)' }}>
            {t('Building and publishing are free for a week. Keep it online for ', 'البناء والنشر مجانيان لمدة أسبوع. أبقِه منشورًا مقابل ')}
            <strong style={{ color: 'var(--u-ink)' }}>
              <Price showVat={false} inline />
            </strong>
          </p>
        </div>
      </section>

      {/* --- how it works ------------------------------------------------------------------- */}
      {/*
        ⚠️ THREE SECTIONS USED TO FOLLOW — "Three steps, one evening", a features band and a pricing
        block — AND THEY ARE DELETED RATHER THAN COLLAPSED. This page has one job: get a description
        into that box and the button pressed. Everything below the fold was answering questions
        nobody had asked yet, and each one gave a visitor another reason to scroll instead of type.
        The price is still stated beside the button, where it is a fact about the decision being made
        rather than a section about itself.
      */}
    </>
  )
}
